import {
  BarChart3,
  BookCopy,
  BookMarked,
  BookOpen,
  BookUp2,
  Boxes,
  CalendarDays,
  ClipboardList,
  CreditCard,
  Globe2,
  GraduationCap,
  HandCoins,
  Heart,
  Landmark,
  LayoutDashboard,
  Library,
  ListChecks,
  Newspaper,
  Printer,
  Repeat2,
  ScanLine,
  Settings2,
  ShoppingCart,
  Sparkles,
  Tags,
  Users,
  Wrench,
} from 'lucide-react';
import type { Permission } from '@lumina/types';
import type { PermissionApi } from '@/lib/auth/AuthProvider';
import type { ActionDef, NavChild, NavGroup, NavItem } from './types';

/**
 * The staff information architecture. This file is the single source of truth for:
 *  - the sidebar and mobile "More" sheet,
 *  - the Cmd/Ctrl+K command palette (navigation entries),
 *  - breadcrumbs.
 * `permission` is "any of"; superlibrarian passes everything. Omit it for items every staff member sees.
 */
const CIRC: readonly Permission[] = ['circulate'];

export const STAFF_NAV: readonly NavGroup[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [{ id: 'dashboard', label: 'Dashboard', to: '/staff', icon: LayoutDashboard, end: true, keywords: ['home', 'overview', 'today'] }],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      {
        id: 'circulation',
        label: 'Circulation',
        to: '/staff/circulation',
        icon: ScanLine,
        permission: CIRC,
        keywords: ['checkout', 'issue', 'return', 'desk', 'loans'],
        children: [
          { id: 'circ-desk', label: 'Circulation desk', to: '/staff/circulation', end: true, keywords: ['checkout', 'check in', 'renew', 'scan'] },
          { id: 'circ-holds', label: 'Holds queue', to: '/staff/circulation/holds', end: true, permission: ['circulate', 'reserveforothers'], keywords: ['reserves', 'requests'] },
          { id: 'circ-pull', label: 'Holds to pull', to: '/staff/circulation/holds/pull-list', permission: CIRC, keywords: ['pick list', 'shelf'] },
          { id: 'circ-transfers', label: 'Transfers', to: '/staff/circulation/transfers', permission: CIRC, keywords: ['in transit', 'branch'] },
          { id: 'circ-overdues', label: 'Overdues', to: '/staff/circulation/overdues', permission: CIRC, keywords: ['late', 'notices'] },
          { id: 'circ-offline', label: 'Offline circulation', to: '/staff/circulation/offline', permission: CIRC, keywords: ['sync', 'queue'] },
          { id: 'circ-articles', label: 'Article requests', to: '/staff/circulation/article-requests', permission: CIRC, keywords: ['photocopy', 'scan'] },
        ],
      },
      {
        id: 'patrons',
        label: 'Patrons',
        to: '/staff/patrons',
        icon: Users,
        permission: ['borrowers'],
        keywords: ['members', 'borrowers', 'readers', 'cards'],
        children: [
          { id: 'patrons-all', label: 'All patrons', to: '/staff/patrons', end: true },
          { id: 'patrons-new', label: 'New patron', to: '/staff/patrons/new', keywords: ['register', 'add member'] },
          { id: 'patrons-import', label: 'Import patrons', to: '/staff/patrons/import', permission: ['import', 'borrowers'] },
          { id: 'patrons-categories', label: 'Categories', to: '/staff/patrons/categories', permission: ['management'] },
          { id: 'patrons-attributes', label: 'Attribute types', to: '/staff/patrons/attributes', permission: ['management', 'parameters'] },
          { id: 'patron-lists', label: 'Patron lists', to: '/staff/patron-lists' },
          { id: 'staff-accounts', label: 'Staff accounts', to: '/staff/staff-accounts', permission: ['permissions'], keywords: ['librarians', 'roles'] },
        ],
      },
      {
        id: 'fines',
        label: 'Fines and fees',
        to: '/staff/fines',
        icon: HandCoins,
        permission: ['updatecharges', 'cash_management'],
        keywords: ['payments', 'charges', 'write off', 'cash'],
        children: [
          { id: 'fines-all', label: 'Outstanding', to: '/staff/fines', end: true, permission: ['updatecharges'] },
          { id: 'fines-registers', label: 'Cash registers', to: '/staff/fines/registers', permission: ['cash_management'] },
          { id: 'fines-daily', label: 'Daily summary', to: '/staff/fines/daily', permission: ['updatecharges', 'cash_management'], keywords: ['end of day', 'collection'] },
        ],
      },
    ],
  },
  {
    id: 'collection',
    label: 'Collection',
    items: [
      {
        id: 'catalog',
        label: 'Catalogue',
        to: '/staff/catalog',
        icon: BookOpen,
        permission: ['catalogue'],
        keywords: ['books', 'biblio', 'records', 'marc', 'search'],
        children: [
          { id: 'catalog-search', label: 'Search catalogue', to: '/staff/catalog', end: true },
          { id: 'catalog-new', label: 'New record', to: '/staff/catalog/new', permission: ['editcatalogue'], keywords: ['add book', 'cataloguing'] },
          { id: 'catalog-import', label: 'Import records', to: '/staff/catalog/import', permission: ['import', 'editcatalogue'], keywords: ['marc', 'z39.50'] },
          { id: 'catalog-export', label: 'Export records', to: '/staff/catalog/export', keywords: ['marc', 'csv'] },
          { id: 'catalog-authorities', label: 'Authorities', to: '/staff/catalog/authorities', permission: ['editauthorities', 'catalogue'], keywords: ['names', 'subjects'] },
          { id: 'items-batch', label: 'Batch item edit', to: '/staff/items/batch', permission: ['tools', 'editcatalogue'], keywords: ['bulk', 'modify'] },
          { id: 'reviews', label: 'Reviews and tags', to: '/staff/reviews', permission: ['editcatalogue'], keywords: ['moderation', 'comments'] },
        ],
      },
      { id: 'inventory', label: 'Inventory', to: '/staff/inventory', icon: Boxes, permission: ['tools'], keywords: ['stocktake', 'shelf check'] },
      { id: 'labels', label: 'Labels and barcodes', to: '/staff/labels', icon: Printer, permission: ['tools'], keywords: ['spine', 'print', 'barcode'] },
      { id: 'lists', label: 'Lists', to: '/staff/lists', icon: ListChecks, permission: ['lists'], keywords: ['shelves', 'collections'] },
      { id: 'courses', label: 'Course reserves', to: '/staff/courses', icon: GraduationCap, permission: ['courses'], keywords: ['syllabus', 'reading list'] },
      { id: 'serials', label: 'Serials', to: '/staff/serials', icon: Newspaper, permission: ['serials'], keywords: ['periodicals', 'subscriptions', 'journals'] },
    ],
  },
  {
    id: 'acquisitions',
    label: 'Acquisitions',
    items: [
      {
        id: 'acquisitions',
        label: 'Acquisitions',
        to: '/staff/acquisitions',
        icon: ShoppingCart,
        permission: ['acquisition'],
        keywords: ['purchase', 'budget', 'vendors', 'orders'],
        children: [
          { id: 'acq-home', label: 'Overview', to: '/staff/acquisitions', end: true },
          { id: 'acq-vendors', label: 'Vendors', to: '/staff/acquisitions/vendors' },
          { id: 'acq-funds', label: 'Funds and budgets', to: '/staff/acquisitions/funds' },
          { id: 'acq-baskets', label: 'Baskets', to: '/staff/acquisitions/baskets' },
          { id: 'acq-orders', label: 'Orders', to: '/staff/acquisitions/orders' },
          { id: 'acq-invoices', label: 'Invoices', to: '/staff/acquisitions/invoices' },
          { id: 'acq-suggestions', label: 'Suggestions', to: '/staff/acquisitions/suggestions', keywords: ['purchase requests'] },
          { id: 'acq-receiving', label: 'Receiving', to: '/staff/acquisitions/receiving' },
        ],
      },
    ],
  },
  {
    id: 'services',
    label: 'Services',
    items: [
      { id: 'ill', label: 'Interlibrary loans', to: '/staff/ill', icon: Repeat2, permission: ['ill'], keywords: ['ill', 'borrowing', 'lending'] },
      { id: 'erm', label: 'E-resources', to: '/staff/erm', icon: Globe2, permission: ['erm'], keywords: ['databases', 'ebooks', 'licences'] },
      { id: 'events', label: 'Events', to: '/staff/events', icon: CalendarDays, permission: ['events'], keywords: ['programmes', 'workshops', 'registration'] },
      { id: 'rooms', label: 'Rooms', to: '/staff/rooms', icon: Landmark, permission: ['events'], keywords: ['study room', 'booking', 'spaces'] },
      { id: 'engagement', label: 'Clubs and challenges', to: '/staff/engagement', icon: Heart, permission: ['clubs'], keywords: ['reading', 'engagement', 'badges'] },
    ],
  },
  {
    id: 'insights',
    label: 'Insights',
    items: [
      {
        id: 'reports',
        label: 'Reports',
        to: '/staff/reports',
        icon: BarChart3,
        permission: ['reports'],
        keywords: ['analytics', 'statistics', 'sql'],
        children: [
          { id: 'reports-all', label: 'All reports', to: '/staff/reports', end: true },
          { id: 'reports-new', label: 'New report', to: '/staff/reports/new' },
          { id: 'reports-sql', label: 'SQL reports', to: '/staff/reports/sql', permission: ['superlibrarian'], keywords: ['query'] },
        ],
      },
    ],
  },
  {
    id: 'system',
    label: 'System',
    items: [
      {
        id: 'tools',
        label: 'Tools',
        to: '/staff/tools',
        icon: Wrench,
        permission: ['tools', 'news', 'parameters', 'management'],
        keywords: ['utilities'],
        children: [
          { id: 'tools-notices', label: 'Notice templates', to: '/staff/tools/notices', end: true, permission: ['tools'], keywords: ['email', 'sms', 'letters'] },
          { id: 'tools-queue', label: 'Message queue', to: '/staff/tools/notices/queue', permission: ['tools'], keywords: ['outbox', 'sent'] },
          { id: 'tools-calendar', label: 'Holidays calendar', to: '/staff/tools/calendar', permission: ['tools', 'management'], keywords: ['closed days'] },
          { id: 'tools-news', label: 'News', to: '/staff/tools/news', permission: ['news'], keywords: ['announcements'] },
          { id: 'tools-cron', label: 'Scheduled jobs', to: '/staff/tools/cron', permission: ['parameters'], keywords: ['cron', 'tasks'] },
          { id: 'tools-uploads', label: 'Uploads', to: '/staff/tools/uploads', permission: ['tools'], keywords: ['files', 'media'] },
        ],
      },
      {
        id: 'admin',
        label: 'Administration',
        to: '/staff/admin',
        icon: Settings2,
        permission: ['management', 'parameters', 'permissions', 'audit', 'import'],
        keywords: ['settings', 'configuration', 'system'],
        children: [
          { id: 'admin-libraries', label: 'Libraries', to: '/staff/admin/libraries', permission: ['management'], keywords: ['branches'] },
          { id: 'admin-item-types', label: 'Item types', to: '/staff/admin/item-types', permission: ['management'] },
          { id: 'admin-locations', label: 'Shelving locations', to: '/staff/admin/locations', permission: ['management'] },
          { id: 'admin-collections', label: 'Collection codes', to: '/staff/admin/collections', permission: ['management'] },
          { id: 'admin-rules', label: 'Circulation rules', to: '/staff/admin/circulation-rules', permission: ['management'], keywords: ['loan period', 'fines', 'renewals'] },
          { id: 'admin-preferences', label: 'System preferences', to: '/staff/admin/preferences', permission: ['parameters'], keywords: ['sysprefs', 'settings'] },
          { id: 'admin-av', label: 'Authorised values', to: '/staff/admin/authorised-values', permission: ['parameters'], keywords: ['lists', 'vocab'] },
          { id: 'admin-staff', label: 'Roles and permissions', to: '/staff/admin/staff', permission: ['permissions'] },
          { id: 'admin-api', label: 'API clients', to: '/staff/admin/api-clients', permission: ['parameters'], keywords: ['oauth', 'integration'] },
          { id: 'admin-webhooks', label: 'Webhooks', to: '/staff/admin/webhooks', permission: ['parameters'] },
          { id: 'admin-audit', label: 'Audit log', to: '/staff/admin/audit', permission: ['audit'], keywords: ['history', 'who changed'] },
          { id: 'admin-system', label: 'System health', to: '/staff/admin/system', permission: ['parameters'], keywords: ['status', 'version'] },
          { id: 'admin-koha', label: 'Koha migration', to: '/staff/admin/koha-import', permission: ['import'], keywords: ['import', 'migrate', 'koha'] },
        ],
      },
    ],
  },
];

/** Quick actions for the command palette ("do" rather than "go"). */
export const STAFF_ACTIONS: readonly ActionDef[] = [
  { id: 'act-checkout', label: 'Check out items', to: '/staff/circulation', icon: ScanLine, permission: CIRC, keywords: ['issue', 'loan', 'scan'], shortcut: 'g c' },
  { id: 'act-checkin', label: 'Check in items', to: '/staff/circulation?tab=checkin', icon: BookUp2, permission: CIRC, keywords: ['return'] },
  { id: 'act-renew', label: 'Renew items', to: '/staff/circulation?tab=renew', icon: Repeat2, permission: CIRC },
  { id: 'act-new-patron', label: 'Register a new patron', to: '/staff/patrons/new', icon: Users, permission: ['borrowers'], keywords: ['add member', 'new card'] },
  { id: 'act-new-record', label: 'Create a bibliographic record', to: '/staff/catalog/new', icon: BookCopy, permission: ['editcatalogue'], keywords: ['add book'] },
  { id: 'act-print-labels', label: 'Print spine labels', to: '/staff/labels', icon: Printer, permission: ['tools'] },
  { id: 'act-new-report', label: 'Build a report', to: '/staff/reports/new', icon: BarChart3, permission: ['reports'] },
  { id: 'act-pay', label: 'Take a payment', to: '/staff/fines', icon: CreditCard, permission: ['updatecharges'], keywords: ['collect fine'] },
  { id: 'act-new-order', label: 'Start an order basket', to: '/staff/acquisitions/baskets', icon: ShoppingCart, permission: ['acquisition'] },
  { id: 'act-public', label: 'Open the public catalogue', to: '/', icon: Library, keywords: ['opac', 'patron view'] },
  { id: 'act-design', label: 'Design system', to: '/staff/design-system', icon: Sparkles, keywords: ['components', 'tokens'] },
  { id: 'act-holds', label: 'Holds waiting for pickup', to: '/staff/circulation/holds', icon: BookMarked, permission: CIRC },
  { id: 'act-notices', label: 'Edit notice templates', to: '/staff/tools/notices', icon: ClipboardList, permission: ['tools'] },
  { id: 'act-tags', label: 'Authority and tag moderation', to: '/staff/reviews', icon: Tags, permission: ['editcatalogue'] },
];

const allowed = (needs: readonly Permission[] | undefined, perms: PermissionApi): boolean => !needs || perms.hasAny(needs);

/** Drop items and children the user cannot use; remove groups and parents left empty. */
export function filterNav(groups: readonly NavGroup[], perms: PermissionApi): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => allowed(item.permission, perms))
        .map((item): NavItem => (item.children ? { ...item, children: item.children.filter((c) => allowed(c.permission, perms)) } : item))
        .filter((item) => !item.children || item.children.length > 0),
    }))
    .filter((group) => group.items.length > 0);
}

export function filterActions(actions: readonly ActionDef[], perms: PermissionApi): ActionDef[] {
  return actions.filter((a) => allowed(a.permission, perms));
}

/** Flat list of every navigable destination (parents and children) for search and the More sheet. */
export function flattenNav(groups: readonly NavGroup[]): { id: string; label: string; to: string; group: string; parent?: string; icon: NavItem['icon']; keywords: readonly string[]; end?: boolean }[] {
  const out: ReturnType<typeof flattenNav> = [];
  for (const group of groups) {
    for (const item of group.items) {
      if (!item.children) {
        out.push({ id: item.id, label: item.label, to: item.to, group: group.label, icon: item.icon, keywords: item.keywords ?? [], end: item.end });
        continue;
      }
      for (const child of item.children) {
        out.push({ id: child.id, label: child.label, to: child.to, group: group.label, parent: item.label, icon: item.icon, keywords: [...(child.keywords ?? []), ...(item.keywords ?? [])], end: child.end });
      }
    }
  }
  return out;
}

/** Does `pathname` belong to this item (for sidebar highlight)? */
export function isNavActive(pathname: string, to: string, end?: boolean): boolean {
  const path = to.split('?')[0] as string;
  return end ? pathname === path : pathname === path || pathname.startsWith(`${path}/`);
}

export type { NavChild, NavGroup, NavItem, ActionDef };

/** Items pinned in the mobile bottom tab bar (the rest live in the More sheet). */
export const MOBILE_TAB_IDS = ['dashboard', 'circulation', 'catalog', 'patrons'] as const;

/** Keyboard shortcuts shown in the cheat sheet. `g` then a letter navigates. */
export const STAFF_SHORTCUTS: readonly { keys: string; label: string; group: string }[] = [
  { keys: 'mod+k', label: 'Open the command palette', group: 'General' },
  { keys: '?', label: 'Show keyboard shortcuts', group: 'General' },
  { keys: 'mod+b', label: 'Collapse or expand the sidebar', group: 'General' },
  { keys: 'Esc', label: 'Close dialogs, menus and the palette', group: 'General' },
  { keys: 'g d', label: 'Go to the dashboard', group: 'Go to' },
  { keys: 'g c', label: 'Go to the circulation desk', group: 'Go to' },
  { keys: 'g k', label: 'Go to the catalogue', group: 'Go to' },
  { keys: 'g p', label: 'Go to patrons', group: 'Go to' },
  { keys: 'g f', label: 'Go to fines and fees', group: 'Go to' },
  { keys: 'Up / Down', label: 'Move between table rows', group: 'Tables' },
  { keys: 'Enter', label: 'Open the focused row', group: 'Tables' },
  { keys: 'Space', label: 'Select the focused row', group: 'Tables' },
  { keys: 'Shift + click', label: 'Select a range of rows', group: 'Tables' },
  { keys: 'Enter', label: 'Submit a scanned or typed barcode', group: 'Circulation desk' },
  { keys: 'Up / Down', label: 'Recall previous barcodes in the scan field', group: 'Circulation desk' },
];
